import { useCallback, useRef } from 'react'
import { useNavigate, useLocation, NavigateOptions } from 'react-router-dom'
import { controlState } from '../store/controlState.ts'
import { drawerState } from '../store/drawerState.ts'
import { getNavDirection, getBasePath } from '../helpers/navigationHelper.ts'

/**
 * A drop-in replacement for useNavigate that wraps navigation in the
 * View Transitions API for animated page transitions.
 *
 * - Sets navigation direction (forward/back/none) on controlState
 * - Sets data-nav-direction on <html> for CSS selectors
 * - Falls back to instant navigation when View Transitions API is unavailable
 * - Skips transitions when drawers/menus are open (to avoid animation conflicts)
 */
export function useViewTransitionNavigate() {
    const navigate = useNavigate()
    const location = useLocation()
    const transitionRef = useRef<ViewTransition | null>(null)
    // Use a ref for pathname to avoid recreating the callback on every route change
    const pathnameRef = useRef(location.pathname)
    pathnameRef.current = location.pathname

    /**
     * Check if any overlay is currently open that would conflict with page transitions.
     */
    const shouldSkipTransition = () => {
        return controlState.menuOpen || controlState.findOpen || drawerState.drawerOpen
    }

    const transitionNavigate = useCallback((
        to: string,
        options?: NavigateOptions & { direction?: 'forward' | 'back' | 'none' }
    ) => {
        const from = getBasePath(pathnameRef.current)
        const toBase = getBasePath(to)

        // Determine direction: explicit override or auto-detect
        const direction = options?.direction ?? getNavDirection(from, toBase)

        // Skip transition if: no API support, overlays open, or 'none' direction on mobile
        const isMobile = controlState.isOnMobileDevice
        const shouldSkip = shouldSkipTransition() || !document.startViewTransition ||
            (direction === 'none' && isMobile)

        if (shouldSkip) {
            navigate(to, options)
            controlState.navDirection = 'none'
            document.documentElement.removeAttribute('data-nav-direction')
            return
        }

        controlState.navDirection = direction
        document.documentElement.setAttribute('data-nav-direction', direction)

        // If a transition is already running, skip it to prevent stacking
        if (transitionRef.current) {
            transitionRef.current.skipTransition()
        }

        const transition = document.startViewTransition!(() => {
            navigate(to, options)
        })

        transitionRef.current = transition

        transition.finished.then(() => {
            transitionRef.current = null
            controlState.navDirection = 'none'
            document.documentElement.removeAttribute('data-nav-direction')
        }).catch(() => {
            // Transition was skipped or errored — clean up
            transitionRef.current = null
            controlState.navDirection = 'none'
            document.documentElement.removeAttribute('data-nav-direction')
        })
    }, [navigate])

    const goBack = useCallback(() => {
        // Skip transition if overlays are open
        if (shouldSkipTransition() || !document.startViewTransition) {
            window.history.back()
            controlState.navDirection = 'none'
            document.documentElement.removeAttribute('data-nav-direction')
            return
        }

        controlState.navDirection = 'back'
        document.documentElement.setAttribute('data-nav-direction', 'back')

        if (transitionRef.current) {
            transitionRef.current.skipTransition()
        }

        const transition = document.startViewTransition(() => {
            window.history.back()
        })

        transitionRef.current = transition

        transition.finished.then(() => {
            transitionRef.current = null
            controlState.navDirection = 'none'
            document.documentElement.removeAttribute('data-nav-direction')
        }).catch(() => {
            transitionRef.current = null
            controlState.navDirection = 'none'
            document.documentElement.removeAttribute('data-nav-direction')
        })
    }, [])

    return { navigate: transitionNavigate, goBack }
}
